import GraphLegend from "$root/components/GraphLegend";
import DonoutCircle from "$root/components/icons/DonoutCircle";
import InfiniteLoader from "$root/components/InfiniteLoader";
import { outlookRepository } from "$root/functional-areas/outlook/repository";
import useOutlookComposition from "$root/hooks/useOutlookComposition";
import { PaletteColors } from "$root/styles/themePalette";
import { type ContextContent, withContext } from "$root/utils";
import { useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import { UniverseFiltersContext } from "$root/widgets-architecture/contexts/filters";
import { useWidgetOptions } from "$root/widgets-architecture/layout/WidgetsMapper/context";
import { Icon } from "@mdotm/shadcn/legacy/components";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ForecastTable from "./ForecastTable";
import LegendExpectedReturns from "$root/assets/images/outlook[preload]/LegendExpectedReturns.svg";

function GraphLegenda(): React.JSX.Element {
	return <img src={LegendExpectedReturns} alt="illustration" />;
}

const ForecastBlock = (props: ContextContent<typeof UniverseFiltersContext>) => {
	const { firstFilter, secondFilter } = props;
	const { assetClass, geography } = useOutlookComposition(firstFilter, secondFilter, "");
	const { t } = useTranslation();
	const [timeSelectorValue, setTimeSelectorValue] = useState("");
	const [_timeSelectorOptions, setTimeSelectorOptions] = useState<Array<{ label: string; value: string }>>([]);

	const _DASHED = (
		<>
			<div className="GREY" />
			<div className="WHITE" />
			<div className="GREY" />
			<div className="WHITE" />
			<div className="GREY" />
			<div className="WHITE" />
		</>
	);

	const forecastQuery = outlookRepository.forecast.useQuery({ assetClass, geography });
	const { data: forecastData, isLoading } = forecastQuery;
	useQueryReactor(forecastQuery, {
		onSuccess: (data) => {
			const uniqueTimeSelectorOptions = [...Array.from(new Set(data.map((item) => item.timeHorizon ?? "-")))]
				.map((item) => ({
					value: item,
					label: item,
				}))
				.sort((a, b) => (a.label ?? "").localeCompare(b.label ?? ""));
			setTimeSelectorOptions(uniqueTimeSelectorOptions);
			setTimeSelectorValue(uniqueTimeSelectorOptions[0].value);
		},
	});

	useWidgetOptions(
		() => ({
			title: t("FORECAST.TITLE"),
			tooltip: t("FORECAST.TOOLTIP_CONTENT"),
		}),
		[t],
	);

	return (
		<>
			{isLoading ? (
				<InfiniteLoader />
			) : (
				forecastData && (
					<div className="flex-1 min-h-0 flex flex-col">
						<ForecastTable
							forecasts={forecastData.filter((item) => item.timeHorizon === timeSelectorValue)}
							firstFilter={props.firstFilter}
							secondFilter={props.secondFilter}
							timeSelectorValue={timeSelectorValue}
						/>
					</div>
				)
			)}
			<GraphLegend>
				<div className="legend-container light more-space book">
					<Icon icon="driver-arrow-center" color={PaletteColors.AZURE} size={10} /> {t("CURRENT_FORECAST")}
				</div>
				<div className="legend-container light more-space book">
					<DonoutCircle className="2" /> {t("PREVIOUS_FORECAST")}
				</div>
				<div className="legend-container">
					<GraphLegenda />
				</div>
			</GraphLegend>
		</>
	);
};

export default withContext(UniverseFiltersContext)(ForecastBlock);
